import { apiFetch } from "./client";

/**
 * Sledljivost LOT-a, u oba smera.
 *
 * Ovo nije `lots.ts`: tamo je prijemna šarža otkupa (`receiving_lot`), ulazna strana. Ovde je LOT
 * izlazne robe, onaj sa deklaracije.
 */

export type OriginKind = "RADNI_NALOG" | "POLUPROIZVOD" | "EKSTRAKCIJA" | "PREVODJENJE";

export type LotHead = {
  id: string;
  designation: string | null;
  productName: string | null;
  erpProductId: string | null;
  producedOn: string | null;
  expiresOn: string | null;
  country: string | null;
  /** Šta je kvalitet rekao o LOT-u; null dok nije ocenjen. */
  qualityStatus?: string | null;
  qualityNote?: string | null;
};

export type OriginNode = {
  level: number;
  /** LOT na čijoj recepturi ovaj red stoji; koren je LOT za koji je upit pokrenut. */
  parentLotId: string;
  lotId: string;
  designation: string | null;
  productName: string | null;
  erpProductId: string | null;
  producedOn: string | null;
  expiresOn: string | null;
  quantity: number | null;
  unit: string | null;
  kind: OriginKind;
  documentNumber: string | null;
  documentDate: string | null;
  recipe: string | null;
};

export type Origin = {
  lot: LotHead;
  /** false znači „u ERP-u nije upisano", a ne „nema porekla". Razlika je bitna i piše se. */
  recorded: boolean;
  truncated: boolean;
  depth: number;
  nodes: OriginNode[];
};

export type UsageCompany = {
  companyId: string;
  companyName: string;
  /** Distinct dispatch notes. */
  deliveries: number;
  /**
   * Empty unless every line has a known quantity in one known unit: kilograms and pieces are not
   * added up, and an unknown quantity is not zero.
   */
  quantity: number | null;
  unit: string | null;
  firstShipped: string | null;
  lastShipped: string | null;
  minSteps: number;
};

export type UsageDelivery = {
  level: number;
  companyId: string;
  companyName: string;
  noteId: string;
  noteNumber: string;
  issueDate: string;
  lotId: string;
  designation: string | null;
  productName: string | null;
  quantity: number | null;
  unit: string | null;
};

export type Usage = {
  lot: LotHead;
  truncated: boolean;
  depth: number;
  /** Date of the latest dispatch note imported from the ERP; the list holds nothing after it. */
  coversTo: string | null;
  companies: UsageCompany[];
  deliveries: UsageDelivery[];
};

export type LotHit = {
  id: string;
  designation: string | null;
  /**
   * Prava oznaka šarže na soji iz ERP-a. `designation` tamo nosi KLASU — doslovno jedno slovo —
   * pa se traži i po ovom polju: od 280 prijemnih šarži po oznaci se nalazi 14, po barkodu svih 280.
   */
  barcode: string | null;
  productName: string | null;
  producedOn: string | null;
  expiresOn: string | null;
  qualityStatus?: string | null;
};

/** Pogoci pretrage i priznanje da ih je bilo više: odsečen spisak mora da kaže da je odsečen. */
export type LotSearch = { rows: LotHit[]; totalAtMost: number; cut: boolean };

export const traceabilityApi = {
  origin: (lotId: string, depth?: number) =>
    apiFetch<Origin>(
      `/traceability/lots/${lotId}/origin${depth == null ? "" : `?depth=${depth}`}`,
    ),

  usage: (lotId: string, depth?: number) =>
    apiFetch<Usage>(
      `/traceability/lots/${lotId}/usage${depth == null ? "" : `?depth=${depth}`}`,
    ),

  search: (q: string) =>
    apiFetch<LotSearch>(`/traceability/lots?q=${encodeURIComponent(q)}`),
};
